import { useEffect, useState } from "react";
import SplashScreen from "@/components/splash-screen";
import { usePublicTenant } from "@/context/PublicTenantProvider";
import { isolateAndHydrateCart } from "@/store/cart/cart-store";

type CartTenantHydrationGateProps = {
  children: React.ReactNode;
};

/**
 * Structural cart isolation gate. Cart readers (badge, footer, cart, checkout)
 * mount only after the current tenant's persist key is hydrated.
 */
export default function CartTenantHydrationGate({
  children,
}: CartTenantHydrationGateProps) {
  const { restaurantId, isReady } = usePublicTenant();
  const rid = restaurantId.trim();
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);

  useEffect(() => {
    if (!isReady || !rid) {
      setHydratedFor(null);
      return;
    }

    let cancelled = false;
    setHydratedFor(null);
    void isolateAndHydrateCart(rid).then(() => {
      if (!cancelled) setHydratedFor(rid);
    });

    return () => {
      cancelled = true;
    };
  }, [isReady, rid]);

  if (!isReady || !rid || hydratedFor !== rid) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
